import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate, useLocation } from 'react-router-dom';
import Landing from './pages/Landing';
import LandingMobile from './pages/LandingMobile';
import MapMobile from './pages/MapMobile';
import Register from './pages/Register';
import RegisterMobile from './pages/RegisterMobile'; // Import Mobile Register
import Admin from './pages/Admin';
import InstructorDash from './pages/InstructorDash';
import InstructorDashMobile from './pages/InstructorDashMobile';
import LearnerDash from './pages/LearnerDash';
import LearnerDashMobile from './pages/LearnerDashMobile'; //Import Mobile Learner Dash
import SetupPassword from './pages/SetupPassword'; 
import About from './pages/About';
import AboutMobile from './pages/AboutMobile'; 
import Contact from './pages/Contact';
import ContactMobile from './pages/ContactMobile'; // 📱 Import Mobile Contact
import FAQ from './pages/FAQ';
import FAQMobile from './pages/FAQMobile';
import TermsMobile from './pages/TermsMobile';

// Branding Asset
import schoolLogo from './school-brand.jpg'; 

// 📱 Helper for mobile vs desktop landing page
function ResponsiveHome() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile ? <LandingMobile /> : <Landing />;
}

// 📱 Helper for mobile vs desktop instructor dashboard
function ResponsiveInstructorDash() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile ? <InstructorDashMobile /> : <InstructorDash />;
}

// 📱 Helper for mobile vs desktop About Us page
function ResponsiveAbout() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile ? <AboutMobile /> : <About />;
}

// 📱 Helper for mobile vs desktop FAQ page
function ResponsiveFAQ() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile ? <FAQMobile /> : <FAQ />;
}

// 📱 Helper for mobile vs desktop Register page
function ResponsiveRegister() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile ? <RegisterMobile /> : <Register />;
}

// 📱 Helper for mobile vs desktop Contact page
function ResponsiveContact() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile ? <ContactMobile /> : <Contact />;
}

function ResponsiveLearnerDash() {
  return <LearnerDashMobile />;
}


function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null);
  const [savedInstructorId, setSavedInstructorId] = useState(5);
  const [isMobileScreen, setIsMobileScreen] = useState(window.innerWidth < 768);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Quick Login Form States
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false); 

  // Forgot Password Modal States
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  // Track screen resize for desktop vs mobile nav visibility
  useEffect(() => {
    const handleResize = () => setIsMobileScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Re-check login status whenever the route changes
  useEffect(() => {
    const lId = localStorage.getItem('learnerId');
    const iId = localStorage.getItem('instructorId');
    const uId = localStorage.getItem('userId');
    const role = localStorage.getItem('userRole');
    const instId = localStorage.getItem('instructorId') || 5;

    setIsLoggedIn(Boolean(lId || iId || uId));
    setUserRole(role);
    setSavedInstructorId(instId);
  }, [location]);

  const handleGlobalLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('needsPasswordReset', data.needsPasswordReset ? 'true' : 'false');
        
        if (data.role === 'admin') {
          localStorage.setItem('userRole', 'admin');
          localStorage.setItem('adminToken', data.token || 'mock-admin-token-secure');
          navigate('/admin');
        } else if (data.role === 'instructor') {
          localStorage.setItem('userRole', 'instructor');
          localStorage.setItem('instructorId', data.id || data.instructorId);
          localStorage.setItem('token', data.token || '');
          navigate('/instructor-dash');
        } else {
          localStorage.setItem('userRole', 'learner');
          localStorage.setItem('userId', data.id || data.userId);
          localStorage.setItem('learnerId', data.id || data.userId);
          localStorage.setItem('userName', data.name || "Learner Driver");
          localStorage.setItem('token', data.token || '');
          navigate(`/learner-dash/${data.instructor_id || 5}`);
        }
      } else {
        alert(data.error || "Login failed.");
      }
    } catch (err) {
      alert("Server error.");
    }
  };

  const handleSendResetLink = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await response.json();

      if (response.ok) {
        alert("✅ If this email exists in our system, a password reset link has been sent.");
        setForgotEmail('');
        setShowForgotModal(false);
      } else {
        alert(`❌ ${data.error || 'Failed to send reset email.'}`);
      }
    } catch (err) {
      alert("❌ Server connection error.");
    }
  };

  const getNavStyle = ({ isActive }) => ({
    color: isActive ? '#ffffff' : '#a0aec0',
    backgroundColor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
    padding: '6px 12px',
    borderRadius: '4px',
    textDecoration: 'none',
    fontWeight: 'bold',
    transition: '0.2s',
    whiteSpace: 'nowrap'
  });

  const getDashboardPath = () => {
    if (userRole === 'admin') return '/admin';
    if (userRole === 'instructor') return '/instructor-dash';
    return `/learner-dash/${savedInstructorId}`;
  };

  return (
    <div>
      {/* --- DESKTOP NAV (Shown only on desktop screens) --- */}
      {!isMobileScreen && (
        <nav style={{ 
          padding: '10px 20px', 
          background: '#1c1e22', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '15px',
          borderBottom: '1px solid #333',
          boxSizing: 'border-box',
          width: '100%'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
            <NavLink to="/" style={{ display: 'flex', alignItems: 'center', marginRight: '5px' }}>
              <img src={schoolLogo} alt="School Logo" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
            </NavLink>
            <NavLink to="/" style={getNavStyle}>Home</NavLink>
            {!isLoggedIn && <NavLink to="/apply" style={getNavStyle}>Register</NavLink>}
            {isLoggedIn && <NavLink to={getDashboardPath()} style={getNavStyle}>Dashboard</NavLink>}
            <NavLink to="/about" style={getNavStyle}>About Us</NavLink>
            <NavLink to="/faq" style={getNavStyle}>FAQ</NavLink>
            <NavLink to="/contact" style={getNavStyle}>Contact Us</NavLink>
          </div>

          {!isLoggedIn && (
            <form onSubmit={handleGlobalLogin} style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input 
                type="email" 
                placeholder="Email" 
                value={loginEmail} 
                autoComplete="email"
                onChange={e => setLoginEmail(e.target.value)}
                required
                style={{ padding: '6px 10px', fontSize: '12px', width: '150px', borderRadius: '4px', border: '1px solid #444', background: '#2a2d34', color: '#fff', boxSizing: 'border-box' }}
              />
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input 
                  type={showPassword ? "text" : "password"} 
                  placeholder="Password" 
                  value={loginPassword} 
                  autoComplete="current-password"
                  onChange={e => setLoginPassword(e.target.value)}
                  required
                  style={{ padding: '6px 28px 6px 10px', fontSize: '12px', width: '120px', borderRadius: '4px', border: '1px solid #444', background: '#2a2d34', color: '#fff', boxSizing: 'border-box' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '6px', background: 'transparent', border: 'none', color: '#a0aec0', cursor: 'pointer', fontSize: '11px', padding: '0' }}
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>
              <button type="submit" style={{ padding: '6px 12px', background: '#0066cc', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}>
                Login
              </button>
              <span onClick={() => setShowForgotModal(true)} style={{ color: '#a0aec0', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}>
                Forgot?
              </span>
            </form>
          )}
        </nav>
      )}

      {/* --- FORGOT PASSWORD MODAL POPUP --- */}
      {showForgotModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ background: '#22252a', padding: '25px', borderRadius: '12px', width: '360px', border: '1px solid #444', color: '#fff', boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
            <h3 style={{ marginTop: 0, color: '#fff', fontSize: '16px' }}>Reset Your Password</h3>
            <p style={{ fontSize: '12px', color: '#a0aec0', marginBottom: '15px', lineHeight: '1.4' }}>
              Enter your registered email address, and we will send you a secure link to set up a new password.
            </p>
            <form onSubmit={handleSendResetLink}>
              <input 
                type="email" 
                placeholder="Enter your email address" 
                value={forgotEmail} 
                onChange={e => setForgotEmail(e.target.value)} 
                required 
                style={{ width: '100%', padding: '9px', marginBottom: '15px', borderRadius: '6px', border: '1px solid #444', background: '#2a2d34', color: '#fff', boxSizing: 'border-box', fontSize: '13px' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" onClick={() => setShowForgotModal(false)} style={{ background: '#4a5568', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                  Cancel
                </button>
                <button type="submit" style={{ background: '#0066cc', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                  Send Reset Link 🚀
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Routes>
        <Route path="/" element={<ResponsiveHome />} />
        <Route path="/about" element={<ResponsiveAbout />} />
        <Route path="/contact" element={<ResponsiveContact />} />
        <Route path="/apply" element={<ResponsiveRegister />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/instructor-dash" element={<ResponsiveInstructorDash />} />
      //  <Route path="/learner-dash/:instructorId" element={<LearnerDash />} />
<Route path="/learner-dash/:instructorId" element={<ResponsiveLearnerDash />} />
        <Route path="/setup-password" element={<SetupPassword />} />
        <Route path="/map" element={<MapMobile />} />
        <Route path="/faq" element={<ResponsiveFAQ />} />
        <Route path="/terms" element={<TermsMobile />} />
      </Routes>
    </div>
  );
}

export default App;